import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  BaseApi,
  FileHistoryModel,
  UploadFile,
  VendorFormatService,
  DetailsResponse,
  OverallResponse,
  Quote
} from 'sustainment-component';
import {
  AcceptDeclinePO,
  IBoxLogModel,
  IOtherProjectList,
  IPriority,
  IProject,
  IProjectActiveInvitation,
  IProjectDraftModel,
  IProjectInitial,
  IProjectList,
  IProjectMultipleInvitation,
  IProjectVisibility,
  TokenResponse,
  UpdatePurchaseOrderFilesDto
} from 'sustainment-component';
import { UserAccountQuery } from '../store/userAccount/user-account.query';
import { VendorQuery } from '../store/vendor/vendor.query';
import { MaliciousFileWarningComponent } from '../components/ui/malicious-file-warning/malicious-file-warning.component';
import { MaliciousFileWarningHeaderComponent } from '../components/ui/malicious-file-warning/header';
import { MaliciousFileWarningFooterComponent } from '../components/ui/malicious-file-warning/footer';
import { DialogService } from 'primeng/dynamicdialog';
import {
  AwardedProject,
  ClosedProject,
  DraftProject,
  ReceivingQuoteProject
} from '../containers/projects/projects-overview/types/types';
import {
  ProjectLineItemHistory,
  ProjectLineItemModel,
  ProjectModel,
  ProjectStatus,
  ProjectUpdateSettingsModel,
  PurchaseOrder
} from '../containers/projects/project-details-view/types/types';
import { SplitLineItem } from '../containers/projects/project-details-view/split-line-item-modal/split-line-item-modal.component';

@Injectable()
export class ProjectsAPI extends BaseApi {
  protected urlBase: string;
  private _relativeUrl = 'project';
  private _vendorName: string;

  public constructor(
    public _http: HttpClient,
    private _userAccountQuery: UserAccountQuery,
    private vendorQuery: VendorQuery,
    private dialogService: DialogService,
    private vendorFormatService: VendorFormatService
  ) {
    super(_http);
    this.urlBase = environment.projectsApiBaseUrl;

    this.vendorQuery
      .select((v) => v.vendor?.name)
      .subscribe((v) => (this._vendorName = v || ''));
  }

  public getProject(projectId: string): Observable<ProjectModel> {
    return this.get<ProjectModel>(this._relativeUrl + `/${projectId}`).pipe(
      map((p) => ({
        ...p,
        memberStatus: p.memberStatus,
        members: p.members.map((m) => ({
          ...m,
          logoUrl: this.vendorFormatService.getLogo(
            m.sustainmentId!,
            m.logo || ''
          ),
          coverUrl: this.vendorFormatService.getCover(
            m.sustainmentId!,
            m.cover || ''
          )
        })),
        requests: p.requests?.map((r) => ({
          ...r,
          requester: {
            ...r.requester,
            logoUrl: this.vendorFormatService.getLogo(
              r.requester.sustainmentId!,
              r.requester.logo || ''
            )
          }
        }))
      }))
    );
  }

  public getProjects(): Observable<ProjectListModel> {
    return this.get<ProjectListModel>(this._relativeUrl);
  }

  public getOtherProjects(): Observable<UserOpportunitiesResponse> {
    return this.get<UserOpportunitiesResponse>(this._relativeUrl + '/other');
  }

  public saveProject(project: IProjectInitial): Observable<IProject> {
    return this.post<IProject>(this._relativeUrl, project);
  }

  public deleteProject(projectId: string): Observable<unknown> {
    return this.delete(this._relativeUrl + `/${projectId}`);
  }

  public updateProject(
    project: ProjectModel & {
      updatedDeliveryDates?: {
        projectLineItemIds: string[];
        actualDeliveryDate: Date;
      }[];
    }
  ): Observable<IProject> {
    return this.put<IProject>(
      this._relativeUrl + `/${project.externalId}`,
      project
    );
  }

  public addTeamMember(projectId: string, userId: string): Observable<void> {
    return this.put<void>(
      this._relativeUrl + `/${projectId}/member/${userId}`,
      {}
    );
  }

  public deleteTeamMember(projectId: string, userId: string): Observable<void> {
    return this.delete(this._relativeUrl + `/${projectId}/member/${userId}`);
  }

  public createDraft(
    body: ProjectCreateRequestModel,
    trackEvent: boolean
  ): Observable<ProjectCreateResponseModel> {
    return this.post<ProjectCreateResponseModel>(
      `drafts?trackEvent=${trackEvent}`,
      body
    );
  }

  public updateDraft(
    projectId: string,
    body: ProjectCreateRequestModel,
    trackEvent: boolean
  ): Observable<void> {
    return this.put<void>(`drafts/${projectId}?trackEvent=${trackEvent}`, body);
  }

  public updateProcurementDraft(
    projectId: string,
    body: UpdateProcurementDraftRequest,
    trackEvent: boolean
  ): Observable<void> {
    return this.put<void>(
      `drafts/procurement/${projectId}?trackEvent=${trackEvent}`,
      body
    );
  }

  public changeMemberStatus(
    projectId: string,
    memberId: string,
    status: number
  ): Observable<void> {
    return this.put<void>(
      this._relativeUrl + `/${projectId}/member/${memberId}/status`,
      { id: status }
    );
  }

  public openDraftProject(
    projectId: string,
    fileCount?: number,
    status?: string
  ): Observable<void> {
    const urlParams = new URLSearchParams();
    if (typeof fileCount === 'number') {
      urlParams.append('fileCount', fileCount.toString());
    }
    if (status) {
      urlParams.append('status', status);
    }

    const url =
      `drafts/${projectId}/open` +
      (urlParams.toString() ? '?' + urlParams.toString() : '');
    return this.put<void>(url, {});
  }

  public updateOpenProject(
    externalId: string,
    project: IProjectVisibility
  ): Observable<IProject> {
    return this.put<IProject>(
      this._relativeUrl + `/open/${externalId}`,
      project
    );
  }

  public getPriorities(): Observable<IPriority[]> {
    return this.get<IPriority[]>(this._relativeUrl + `/priorities`);
  }

  public initiateProject(project: ProjectModel): Observable<void> {
    return this.post(
      this._relativeUrl + `/${project.externalId}/initiate`,
      project
    );
  }

  //Vendor invite
  public inviteVendor(
    projectId: string,
    sustainmentId: string,
    message?: string,
    isNotifyingSupplier?: boolean
  ): Observable<void> {
    return this.post(this._relativeUrl + `/${projectId}/vendor`, {
      sustainmentId: sustainmentId,
      message,
      shouldNotifySupplier: isNotifyingSupplier ?? false
    });
  }

  public intentToQuote(projectId: string, message?: string): Observable<void> {
    let body = {};

    if (message) body = { message };

    return this.put(this._relativeUrl + `/${projectId}/intent-quote`, body);
  }

  public noQuote(projectId: string): Observable<void> {
    return this.put(this._relativeUrl + `/${projectId}/intent-no-quote`, {});
  }

  public validateUniqueName(projectName: string): Observable<boolean> {
    return this.get(
      `${this._relativeUrl}/project-name-unique?projectName=${projectName}`
    );
  }

  public leaveProject(projectId: string): Observable<unknown> {
    return this.delete(this._relativeUrl + `/${projectId}/leave`);
  }

  public noQuoteProject(
    projectId: string,
    message: string
  ): Observable<unknown> {
    return this._http.delete(this.urlBase + `project/${projectId}/no-quote`, {
      body: { message }
    });
  }

  public openQuote(projectId: string): Observable<unknown> {
    return this._http.put(this.urlBase + `project/${projectId}/no-quote`, {
      body: {}
    });
  }

  public settingProject(
    projectId: string,
    model: ProjectUpdateSettingsModel
  ): Observable<void> {
    return this.post(this._relativeUrl + `/${projectId}/settings`, model);
  }

  public logBoxEvent(projectId: string, model: IBoxLogModel): Observable<void> {
    return this.post(
      this._relativeUrl + `/collaboration/${projectId}/log`,
      model
    );
  }

  public getProjectUnread(projectId: string): Observable<unknown> {
    return this.get(this._relativeUrl + `/unread/${projectId}`);
  }

  public getProjectPendingCount(): Observable<{ total: number }> {
    return this.get(this._relativeUrl + '/other/pending-count');
  }

  public removeVendorInvitation(
    projectId: string,
    vendorOdysseusId: string
  ): Observable<void> {
    return this.delete(
      `${this._relativeUrl}/${projectId}/vendor-invite/${vendorOdysseusId}`
    );
  }

  public getActiveProjectsInvitation(
    removeVendorId?: string
  ): Observable<IProjectActiveInvitation[]> {
    let baseUrl = `${this._relativeUrl}/active-project-invitation`;

    if (removeVendorId) baseUrl = `${baseUrl}?removeVendorId=${removeVendorId}`;

    return this.get<IProjectActiveInvitation[]>(baseUrl);
  }

  public inviteMultipleVendors(
    body: IProjectMultipleInvitation[]
  ): Observable<void> {
    const baseUrl = `${this._relativeUrl}/invite-multiple-vendors`;

    return this.post(baseUrl, body);
  }

  public getUploadToken(
    projectId: string,
    memberId: string
  ): Observable<{ token: string; folderId: string }> {
    return this.post(
      `${this._relativeUrl}/${projectId}/upload-token`,
      `"${memberId}"`
    );
  }

  public getUploadTokensForAllMembers(
    projectId: string
  ): Observable<ProjectMembersToken[]> {
    return this.get(`${this._relativeUrl}/${projectId}/tokens`);
  }

  public getProjectUploadToken(projectId: string): Observable<string> {
    return this.get(`${this._relativeUrl}/${projectId}/upload-token`);
  }

  public downloadFile(
    projectId: string,
    fileId: string
  ): Observable<{ url: string; scanResult: string } | null> {
    return this.get<{ url: string; scanResult: string }>(
      `${this._relativeUrl}/${projectId}/download/${fileId}`
    ).pipe(
      map((res) => {
        if (res.scanResult === 'Malicious') {
          this.dialogService.open(MaliciousFileWarningComponent, {
            templates: {
              header: MaliciousFileWarningHeaderComponent,
              footer: MaliciousFileWarningFooterComponent
            }
          });
          res.url = '';
        }
        return res;
      }),
      catchError((error) => {
        if (error.status === 400) return of(null);
        return throwError(() => error);
      })
    );
  }

  public getProjectTeam(projectId: string): Observable<string[]> {
    return this.get(`${this._relativeUrl}/${projectId}/team`);
  }

  public getFileHistory(
    projectId: string,
    userId: string
  ): Observable<FileHistoryModel[]> {
    return this.get(`${this._relativeUrl}/${projectId}/files/${userId}`);
  }

  public deleteFile(projectId: string, fileId: string): Observable<void> {
    return this.delete(`${this._relativeUrl}/${projectId}/files/${fileId}`);
  }

  public editAwardProject(
    projectId: string,
    memberId: string,
    data: { amount: number; deliverydate: Date }
  ): Observable<void> {
    return this.put(`${this._relativeUrl}/award`, {
      projectId,
      memberId,
      amount: data?.amount,
      deliveryDate: data?.deliverydate
    });
  }

  public completeProject(
    projectId: string,
    reqData: {
      isCompleted?: boolean;
      projectRating?: number;
      meetTimeExpectations?: boolean;
      meetBudgetExpectations?: boolean;
      metObjective?: boolean;
      closedReason?: string;
      supplierMessage?: string;
      closedStatus?: ProjectStatus;
      deliveryDates?: {
        projectLineItemIds: string[];
        actualDeliveryDate: Date;
      }[];
    }
  ): Observable<void> {
    return this.post(`${this._relativeUrl}/complete`, {
      projectId,
      complete: reqData.isCompleted,
      rating: reqData.projectRating,
      timeExpectationMet: reqData.meetTimeExpectations,
      budgetExpectationMet: reqData.meetBudgetExpectations,
      metObjective: reqData.metObjective,
      closedReason: reqData.closedReason,
      supplierMessage: reqData.supplierMessage,
      closedStatus: reqData.closedStatus,
      updatedDeliveryDates: reqData.deliveryDates
    });
  }

  public getChatToken(projectId: string): Observable<TokenResponse> {
    return this.get<TokenResponse>(`project-chat/${projectId}/token`);
  }

  public getPerformanceDetails(
    sustainmentId: string
  ): Observable<DetailsResponse[]> {
    return this.get<DetailsResponse[]>(
      `${this._relativeUrl}/performance/${sustainmentId}`
    );
  }

  public getPerformanceOverall(): Observable<OverallResponse[]> {
    return this.get<OverallResponse[]>(
      `${this._relativeUrl}/performance/overall`
    );
  }

  public getQuotes(projectId: string): Observable<Quote[]> {
    return this.get<Quote[]>(`quote/project/${projectId}`);
  }

  public addQuote(
    memberSustainmentId: string,
    quote: Quote,
    skipChatNotification: boolean = false
  ): Observable<Quote> {
    return this.post<Quote>(
      `quote/member/${memberSustainmentId}?skipChatNotification=${skipChatNotification}`,
      quote
    );
  }

  public addQuoteWithExternalId(
    memberSustainmentId: string,
    projectId: string,
    quote: Omit<Quote, 'projectId'>,
    skipChatNotification: boolean = false
  ): Observable<Quote> {
    return this.post<Quote>(
      `quote/${projectId}/member/${memberSustainmentId}?skipChatNotification=${skipChatNotification}`,
      quote
    );
  }

  public getPurchaseOrder(projectId: string): Observable<PurchaseOrder[]> {
    return this.get<PurchaseOrder[]>(`PurchaseOrder/project/${projectId}`).pipe(
      map((pos) =>
        pos.sort((a, b) => {
          const aCreatedDate = new Date(a.createdAt!);
          const bCreatedDate = new Date(b.createdAt!);

          return bCreatedDate.getTime() - aCreatedDate.getTime();
        })
      )
    );
  }

  public addPurchaseOrder(
    po: PurchaseOrder | Omit<PurchaseOrder, 'quoteId'>
  ): Observable<PurchaseOrder> {
    return this.post<PurchaseOrder>(`PurchaseOrder/`, po);
  }

  public respondPo(
    dto: AcceptDeclinePO & {
      acknowledgedDate?: string;
    }
  ): Observable<PurchaseOrder> {
    return this.post('PurchaseOrder/respond', dto);
  }

  public updatePurchaseOrderFiles(
    dto: UpdatePurchaseOrderFilesDto
  ): Observable<void> {
    return this.put('PurchaseOrder/update-files', dto);
  }

  public getReceivingQuotesProjects(): Observable<ReceivingQuoteProject[]> {
    return this.get('project/quoting');
  }

  public getDraftProjects(
    projectType: 'sourcing' | 'procurement' = 'sourcing'
  ): Observable<DraftProject[]> {
    return this.get(`project/draft?projectType=${projectType}`);
  }

  public getAwardedProjects(): Observable<AwardedProject[]> {
    return this.get('project/awarded');
  }

  public getClosedProjects(
    projectType: 'sourcing' | 'procurement' = 'sourcing'
  ): Observable<ClosedProject[]> {
    return this.get(`project/closed?projectType=${projectType}`);
  }

  public getDraftBoxUpload(): Observable<{ token: string; folderId: string }> {
    return this.get<{ token: string; folderId: string }>('drafts/upload-token');
  }

  public getItarSetting(projectId: string): Observable<boolean> {
    return this.get(`project/${projectId}/itar`);
  }

  public awardProjectV2(body: AwardProjectRequestModel): Observable<string> {
    return this.post<string>(`project-award/v2`, body);
  }

  public markSupplierOpportunityWon(body: {
    deliveryDate: Date;
    amount: number;
    projectExternalId: string;
  }): Observable<void> {
    return this.post<void>(`project-award/supplier-opportunity-won`, body);
  }

  public updateLineItems(
    projectId: string,
    requestData: UpdateLineItemsRequestModel
  ): Observable<void> {
    return this.post<void>(`project/${projectId}/lineitems`, requestData);
  }

  /**
   * Endpoint for sending a PO file to the BE for AirParser to parse and
   * get parsed data back to prefill portions of a procurement project draft
   */
  public uploadPOFileAndParseForProcurementProject(
    uploadedFile: UploadFile,
    draftProjectExternalId: string
  ): Observable<PoParserProcurementDraftResponse> {
    const formData = new FormData();
    const file = uploadedFile.file as unknown as File;

    if (!file || !(file instanceof File)) {
      throw new Error(`Invalid file object: ${JSON.stringify(file)}`);
    }

    formData.append('file', file, file.name);

    return this.post<PoParserProcurementDraftResponse>(
      `part/airparser/project-po-file-parse/${draftProjectExternalId}?fileId=${uploadedFile.fileId}`,
      formData
    );
  }

  public splitLineItem(
    projectId: string,
    request: SplitLineItem
  ): Observable<void> {
    return this.put<void>(
      `${this._relativeUrl}/${projectId}/split-line-item`,
      request
    );
  }

  public editDeliveryDate(
    projectId: string,
    request: EditDeliveryDateRequest
  ): Observable<void> {
    return this.put<void>(
      `${this._relativeUrl}/${projectId}/edit-delivery-date`,
      request
    );
  }
}

interface ProjectListModel {
  projects: IProjectList[];
  spaceUsed: number;
  spaceAmount: number;
}

export interface UserOpportunitiesResponse {
  closed: IOtherProjectList[];
  inProgress: IOtherProjectList[];
  lost: IOtherProjectList[];
  invitations: IOtherProjectList[];
  quoting: IOtherProjectList[];
  won: IOtherProjectList[];
}

export interface ProjectCreateRequestModel {
  name: string;
  description?: string;
  quoteDueDate?: Date | null;
  awardedDate?: Date | null;
  team?: string[];
  shortlist?: string[];
  purchaseOrder?: ProjectCreationPurchaseOrder;
  awardedSupplier?: string | null;
  settings?: ProjectCreationSettings;
  feedback?: ProjectCreationQuestionnaire;
  draftType: ProjectCreationTypeEnum;
  projectType: ProjectType;
  files?: string[];
  customerSustainmentId?: string;
  supplierIntroMessage?: string;
  lineItems?: ProjectLineItemModel[];
}

export interface ProjectCreateResponseModel {
  externalId: string;
  name: string;
}

interface ProjectCreationPurchaseOrder {
  purchaseOrderNumber?: string;
  purchaseOrderIssueDate?: Date | string;
  deliveryDate: Date;
  pricing: number;
  files: ProjectCreationPurchaseOrderFile[];
}

interface ProjectCreationPurchaseOrderFile {
  fileId: string;
  fileName: string;
}

interface ProjectCreationSettings {
  itarCertificationRequired: boolean;
  signedNDARequired: boolean;
  memberCanDownloadFiles: boolean;
  requireLoginToViewFiles?: boolean;
}

interface ProjectCreationQuestionnaire {
  rating: number;
  timeExpectationMet: boolean;
  budgetExpectationMet: boolean;
}

enum ProjectCreationTypeEnum {
  Private,
  Public,
  ToBeAwarded,
  Started,
  Delivered
}

enum ProjectType {
  Sourcing,
  Procurement
}

export interface ProjectMembersToken {
  sustainmentId: string;
  token: { token: string; folderId: string };
}

export interface AwardProjectRequestModel {
  requiredDate: Date;
  amount: number;
  supplierWinningMessage: string;
  otherSuppliersMessage: string;
  quoteId: number;
  projectExternalId: string;
  supplierSustainmentId: string;
  files: { fileId: string; fileName: string }[];
}

export type UpdateLineItemsRequestModel = {
  lineItems: Array<
    Omit<ProjectLineItemModel, 'projectLineItemHistories'> & {
      projectLineItemHistories: Partial<ProjectLineItemHistory>[];
    }
  >;
  supplierMessage: string;
  updatedLineItemIds: string[];
  processDates: boolean;
};

export type PoParserProcurementDraftResponse = {
  draftProject: IProjectDraftModel;
};

export type UpdateProcurementDraftRequest = {
  name: string;
  description: string;
  awardedDate?: Date;
  team: string[];
  purchaseOrder: ProjectCreationPurchaseOrder;
  awardedSupplier?: string;
  draftType: ProjectCreationTypeEnum;
  parts: UpdateProcurementDraftPart[];
  feedback?: ProjectCreationQuestionnaire;
};

export type UpdateProcurementDraftPart = {
  partNumber: string;
  partDescription: string;
  revisionNumber: string;
  quantity: number;
  unitCost: number;
  lineTotal: number;
  estimatedDeliveryDate?: Date;
  actualDeliveryDate?: Date;
  notes: string;
  isInPartsLibrary: boolean;
  isRevisionValid: boolean;
};

export type EditDeliveryDateRequest = {
  supplierMessage: string;
  editedDeliveryDates: LineItemEditedDeliveryDate[];
};

export type LineItemEditedDeliveryDate = {
  lineItemId: string;
  deliveryDate: Date | null;
};
